import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { MapPin, Car, Bike, Lightbulb, Users, TreePine, Zap, Video, Toilet, UtensilsCrossed, Info } from "lucide-react";

const parkFeatures = [
  {
    id: 1,
    title: "New Toilet Facilities",
    description: "Modern, accessible toilet facilities with baby change rooms and family-friendly design",
    icon: Toilet,
    category: "Essential Services",
    priority: "High",
    estimatedCompletion: "Phase 1",
    benefits: ["Improved accessibility", "Better hygiene standards", "Family convenience"]
  },
  {
    id: 2,
    title: "Additional Car Parking",
    description: "50 new parking spaces including accessible parking and electric vehicle charging stations",
    icon: Car,
    category: "Infrastructure",
    priority: "High", 
    estimatedCompletion: "Phase 1",
    benefits: ["Reduced street parking pressure", "Better accessibility", "EV support"]
  },
  {
    id: 3,
    title: "Bike Racks & Storage",
    description: "Secure bike racks and covered storage areas to encourage cycling to the park",
    icon: Bike,
    category: "Sustainable Transport",
    priority: "Medium",
    estimatedCompletion: "Phase 2",
    benefits: ["Promotes cycling", "Reduces car dependency", "Secure bike storage"]
  },
  {
    id: 4,
    title: "Enhanced Lighting",
    description: "LED pathway lighting and security lighting to improve safety and extend park usage hours",
    icon: Lightbulb,
    category: "Safety & Security",
    priority: "High",
    estimatedCompletion: "Phase 1",
    benefits: ["Improved safety", "Extended usage hours", "Energy efficient"]
  },
  {
    id: 5,
    title: "Outdoor Movie Screen",
    description: "Large outdoor cinema screen with amphitheater-style seating for community events",
    icon: Video,
    category: "Recreation",
    priority: "Medium",
    estimatedCompletion: "Phase 2",
    benefits: ["Community entertainment", "Cultural events", "Family activities"]
  },
  {
    id: 6,
    title: "Playground Upgrade",
    description: "Modern inclusive playground equipment suitable for all ages and abilities",
    icon: Users,
    category: "Recreation",
    priority: "High",
    estimatedCompletion: "Phase 1",
    benefits: ["Inclusive design", "Age-appropriate equipment", "Safety improvements"]
  },
  {
    id: 7,
    title: "Native Tree Planting",
    description: "Additional native trees for shade, wildlife habitat, and environmental benefits",
    icon: TreePine,
    category: "Environment",
    priority: "Medium",
    estimatedCompletion: "Phase 2",
    benefits: ["Environmental benefits", "Wildlife habitat", "Natural shade"]
  },
  {
    id: 8,
    title: "Picnic Areas",
    description: "New covered picnic areas with BBQ facilities and outdoor dining spaces",
    icon: UtensilsCrossed,
    category: "Recreation",
    priority: "Medium",
    estimatedCompletion: "Phase 2",
    benefits: ["Family gatherings", "Community events", "Weather protection"]
  }
];

const implementationPhases = [
  {
    phase: "Phase 1 - Essential Infrastructure",
    timeline: "Year 1",
    description: "Priority improvements focusing on essential services and safety",
    features: ["New Toilet Facilities", "Additional Car Parking", "Enhanced Lighting", "Playground Upgrade"],
    status: "Planning"
  },
  {
    phase: "Phase 2 - Recreation & Environment",
    timeline: "Year 2-3",
    description: "Enhancement features for recreation and environmental improvements",
    features: ["Outdoor Movie Screen", "Bike Racks & Storage", "Native Tree Planting", "Picnic Areas"],
    status: "Design Development"
  }
];

const keyBenefits = [
  {
    category: "Community Health & Wellbeing",
    benefits: [
      "Increased physical activity opportunities",
      "Better accessibility for all community members",
      "Enhanced safety for evening and early morning users",
      "Spaces for community gatherings and events"
    ]
  },
  {
    category: "Environmental Sustainability",
    benefits: [
      "Increased tree canopy and biodiversity",
      "Electric vehicle charging infrastructure",
      "Promotion of cycling and sustainable transport",
      "Energy-efficient LED lighting systems"
    ]
  },
  {
    category: "Economic & Social Impact",
    benefits: [
      "Increased property values in surrounding areas",
      "Job creation during construction phases",
      "Enhanced community pride and social cohesion",
      "Attraction for families and new residents"
    ]
  }
];

const getPriorityBadgeColor = (priority) => {
  switch (priority) {
    case "High":
      return "bg-red-100 text-red-800 hover:bg-red-100";
    case "Medium":
      return "bg-yellow-100 text-yellow-800 hover:bg-yellow-100";
    case "Low":
      return "bg-green-100 text-green-800 hover:bg-green-100";
    default:
      return "bg-gray-100 text-gray-800 hover:bg-gray-100";
  }
};

const getCategoryColor = (category) => {
  switch (category) {
    case "Essential Services":
      return "bg-blue-100 text-blue-800 hover:bg-blue-100";
    case "Infrastructure":
      return "bg-purple-100 text-purple-800 hover:bg-purple-100";
    case "Recreation":
      return "bg-green-100 text-green-800 hover:bg-green-100";
    case "Safety & Security":
      return "bg-orange-100 text-orange-800 hover:bg-orange-100";
    case "Environment":
      return "bg-emerald-100 text-emerald-800 hover:bg-emerald-100";
    default:
      return "bg-gray-100 text-gray-800 hover:bg-gray-100";
  }
};

export function PlanDetailsTab() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1>Park Master Plan Details</h1>
          <p className="text-muted-foreground mt-1">
            Comprehensive overview of proposed park improvements and enhancements
          </p>
        </div>
        <Badge variant="secondary" className="flex items-center gap-1">
          <Info className="h-3 w-3" />
          Public Consultation Draft
        </Badge>
      </div>

      {/* Implementation Phases */}
      <Card>
        <CardHeader>
          <CardTitle>Implementation Phases</CardTitle>
          <CardDescription>
            The park improvements will be delivered in two main phases over 3 years
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {implementationPhases.map((phase, index) => (
              <div key={index} className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">{phase.phase}</h3>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{phase.timeline}</Badge>
                    <Badge variant="secondary">{phase.status}</Badge>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">{phase.description}</p>
                <div className="flex flex-wrap gap-2">
                  {phase.features.map((feature, featureIndex) => (
                    <Badge key={featureIndex} variant="outline" className="text-xs">
                      {feature}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Proposed Features */}
      <Card>
        <CardHeader>
          <CardTitle>Proposed Park Features</CardTitle>
          <CardDescription>
            Detailed breakdown of all planned improvements and new facilities
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {parkFeatures.map((feature) => {
              const Icon = feature.icon;
              return (
                <div key={feature.id} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className="h-5 w-5 text-blue-600" />
                      <h3 className="font-medium">{feature.title}</h3>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Badge 
                        variant="secondary" 
                        className={getPriorityBadgeColor(feature.priority)}
                      >
                        {feature.priority}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Badge 
                      variant="outline" 
                      className={getCategoryColor(feature.category)}
                    >
                      {feature.category}
                    </Badge>
                    <Badge variant="outline">
                      {feature.estimatedCompletion}
                    </Badge>
                  </div>
                  
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                  
                  <div className="space-y-1">
                    <span className="text-sm font-medium">Key Benefits:</span>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      {feature.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-center gap-1">
                          <span className="w-1 h-1 bg-current rounded-full"></span>
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Key Benefits */}
      <Card>
        <CardHeader>
          <CardTitle>Expected Community Benefits</CardTitle>
          <CardDescription>
            How these improvements will benefit the local community and environment
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {keyBenefits.map((benefitCategory, index) => (
              <div key={index} className="space-y-3">
                <h3 className="font-medium text-center">{benefitCategory.category}</h3>
                <div className="space-y-2">
                  {benefitCategory.benefits.map((benefit, benefitIndex) => (
                    <div key={benefitIndex} className="p-2 bg-muted/30 rounded text-sm text-center">
                      {benefit}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Next Steps */}
      <Card>
        <CardHeader>
          <CardTitle>Next Steps</CardTitle>
          <CardDescription>
            How you can get involved and what happens next in the planning process
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg text-center space-y-2">
              <h4 className="font-medium">Share Your Feedback</h4>
              <p className="text-sm text-muted-foreground">
                Complete our community survey to share your thoughts on these proposed improvements
              </p>
              <Button variant="outline" size="sm">Take Survey</Button>
            </div>
            <div className="p-4 border rounded-lg text-center space-y-2">
              <h4 className="font-medium">Attend Public Meeting</h4>
              <p className="text-sm text-muted-foreground">
                Join us for public consultation meetings to discuss the plan in detail
              </p>
              <Button variant="outline" size="sm">View Dates</Button>
            </div>
          
          </div>
        </CardContent>
      </Card>
    </div>
  );
}